import type { SectionTitle } from "@/generated/sb";
import { SectionTitleButtonItem } from "@repo/ui-parkmobile/SectionTitle";
import { trackCtaClick } from "./trackingScripts";

export { trackSearchLocation } from "./trackingScripts";

interface RichtextNode {
  type: string;
  text?: string;
  content?: RichtextNode[];
}

interface Richtext {
  content?: RichtextNode[];
}

export function extractTextFromRichtext(richtext?: Richtext): string {
  if (!richtext?.content) return "";

  const extractFromNode = (node: RichtextNode): string => {
    if (node.type === "text") {
      return node.text || "";
    }
    if (node.content && Array.isArray(node.content)) {
      return node.content.map(extractFromNode).join(" ");
    }
    return "";
  };

  return richtext.content.map(extractFromNode).join(" ").trim();
}

export function getSectionNameFromTypography(
  typography?: Array<{
    component: string;
    content?: string;
    [key: string]: any;
  }>
): string {
  if (!typography) return "";

  const titleItem = typography.find((item) => item.component === "title");
  return titleItem?.content || "";
}

export function getContentDescriptionFromTypography(
  typography?: Array<{ component: string; content?: any; [key: string]: any }>
): string {
  if (!typography) return "";

  const textItem = typography.find(
    (item) => item.component === "simpleRichText"
  );

  if (!textItem || !textItem.content) return "";

  try {
    const content = textItem.content;
    if (typeof content === "string") return content;

    if (
      content &&
      typeof content === "object" &&
      "content" in content &&
      Array.isArray(content.content)
    ) {
      return content.content
        .map((block: any) => {
          if (block.type === "paragraph" && Array.isArray(block.content)) {
            return block.content
              .map((node: any) => {
                if (node.type === "text" && node.text) {
                  return node.text;
                }
                return "";
              })
              .join("");
          }
          return "";
        })
        .join(" ")
        .trim();
    }

    return "";
  } catch (error) {
    console.error("Error extracting content description:", error);
    return "";
  }
}

export function getSectionName(
  typography?: SectionTitle[],
  fallback?: string
): string {
  return (
    typography?.[0]?.typography?.find((t) => t.component === "title")
      ?.content ||
    fallback ||
    ""
  );
}

export function getContentDescription(
  typography?: SectionTitle[]
): string {
  if (!typography?.[0]?.typography) return "";

  const textItem = typography[0].typography.find(
    (t) => t.component === "simpleRichText"
  );

  if (!textItem || !("content" in textItem)) return "";

  try {
    const content = textItem.content;
    if (typeof content === "string") return content;

    if (
      content &&
      typeof content === "object" &&
      "content" in content &&
      Array.isArray(content.content)
    ) {
      return content.content
        .map((block: any) => {
          if (block.type === "paragraph" && Array.isArray(block.content)) {
            return block.content
              .map((node: any) => {
                if (node.type === "text" && node.text) {
                  return node.text;
                }
                return "";
              })
              .join("");
          }
          return "";
        })
        .join(" ")
        .trim();
    }

    return "";
  } catch (error) {
    console.error("Error extracting content description:", error);
    return "";
  }
}

export function injectButtonAnalytics(
  buttons: SectionTitleButtonItem[] | undefined,
  sectionName: string,
  contentDescription?: string,
  config: string = "button"
): SectionTitleButtonItem[] | undefined {
  if (!buttons) return buttons;

  // Resolvers may yield null for unusable buttons (e.g. a Navattic button
  // without a valid URL); never let that reach the type checks below.
  return buttons.filter((button) => button != null).map((button) => {
    if (button.type === "searchField" || button.type === "videoPlayerButton") {
      return button;
    }

    const originalOnClick = button.onClick;

    if (button.analyticsEvent === "download_app_click") {
      return {
        ...button,
        onClick: () => {
          trackCtaClick({
            event: "download_app_click",
            section: sectionName,
            ctaText: button.text || "",
            config: config,
            contentDescription: contentDescription || "",
            link: button.href || button.navatticUrl || "",
          });

          originalOnClick?.();
        },
      };
    }

    if (
      button.analyticsEvent === "ctaClick" ||
      !button.analyticsEvent ||
      button.analyticsEvent === ""
    ) {
      return {
        ...button,
        onClick: () => {
          trackCtaClick({
            event: "ctaClick",
            section: sectionName,
            ctaText: button.text || "",
            config: config,
            contentDescription: contentDescription || "",
            link: button.href || button.navatticUrl || "",
          });

          originalOnClick?.();
        },
      };
    }

    return button;
  });
}
